import type { EvaluationCriteria, EvaluationRelevantInstance, EvaluationResults } from '~/types/EvaluationResults';
import { evaluateCriteriaTree } from '~/utils/evaluation.utils';
import { compareReviewTestBaseline } from '~/utils/review-test-baseline';

/** Apply the same derived parent/instance values to historical expectations as to live results. */
export function expectedResultsForComparison(result: EvaluationResults): EvaluationResults {
  const copy = { ...result, evaluationResult: JSON.parse(JSON.stringify(result.evaluationResult ?? [])) };
  evaluateCriteriaTree(copy);
  return copy;
}

const tokenKey = (instance: EvaluationRelevantInstance) => {
  const tokens = instance.tokens ?? [];
  // Historical highlighted spans sometimes include adjacent spaces/punctuation.
  const words = tokens.filter(token => /[\p{L}\p{N}]/u.test(token.text ?? ''));
  return tokens.length ? JSON.stringify((words.length ? words : tokens).map(token => [token.id, token.text])) : null;
};

/** Match the rubric and exact target span, independently of provider-generated R1/R2 IDs. */
export function compareEvaluationResults(actual: EvaluationResults, expected: EvaluationResults): boolean {
  if (expected.reviewBaseline) return compareReviewTestBaseline(actual, expected.reviewBaseline);
  if (actual.status === 'failed' || actual.status === 'processing') return false;
  const normalized = expectedResultsForComparison(expected);
  if (!actual.evaluationResult?.length && normalized.evaluationResult?.length) return false;
  evaluateCriteriaTree(actual);
  let matches = true;
  const compareCriteria = (nodes: EvaluationCriteria[], expectations: EvaluationCriteria[], inheritedTarget: string | null = null) => {
    for (const node of nodes ?? []) {
      const sameUid = expectations.filter(e => e.uid && e.uid === node.uid);
      const candidates = sameUid.length ? sameUid : expectations.filter(e => e.config?.text === node.config?.text);
      const reference = candidates.length === 1 ? candidates[0] : undefined;
      node.isUnexpected = !reference || node.passed !== reference.passed;
      if (node.isUnexpected) matches = false;

      // Jev emits a passing word again underneath the check. It carries no
      // additional assessment and is hidden by the metric's existing settings.
      const meaningful = (criterion: EvaluationCriteria, instance: EvaluationRelevantInstance) => !(criterion.id === 'jev_word_check'
        && criterion.config?.shouldShowInstances === false && !instance.criteria?.length
        && inheritedTarget !== null && tokenKey(instance) === inheritedTarget);
      const expectedInstances = (reference?.relevantInstances ?? []).filter(i => meaningful(reference!, i));
      for (const instance of node.relevantInstances ?? []) {
        instance.isUnexpected = false;
        if (!meaningful(node, instance)) continue;
        const target = tokenKey(instance);
        const candidates = expectedInstances.filter(e => target !== null ? tokenKey(e) === target : e.uid === instance.uid);
        const expectedInstance = candidates.length === 1 ? candidates[0] : undefined;
        instance.isUnexpected = !expectedInstance || instance.valid !== expectedInstance.valid;
        if (instance.isUnexpected) matches = false;
        compareCriteria(instance.criteria ?? [], expectedInstance?.criteria ?? [], target);
      }
    }
  };
  compareCriteria(actual.evaluationResult ?? [], normalized.evaluationResult ?? []);
  return matches;
}
